/* SEARCH-TAG: page-template  */
import React, { useLayoutEffect, useContext } from 'react';
import { graphql } from 'gatsby';
import { shape, arrayOf } from 'prop-types';
import styled from 'styled-components';
import { getImage } from 'gatsby-plugin-image';
import HeadTags from 'TMComponents/HeadTags';
import Main from 'TMComponents/Layout/Main';
import Canonical from 'TMComponents/Canonical';
import { BreadcrumbsContext } from 'TMComponents/Breadcrumbs';
import { BannerContext } from 'TMComponents/Banner';
import { LayoutContext } from 'TMComponents/Layout';
import Content from 'TMComponents/Content';
import NavLink from 'TMComponents/NavLink';
import Arrow from 'TMComponents/Icons/Arrow';
import { spacing } from 'TMDesignSystem/spacing';
import borderRadius from 'TMDesignSystem/borders';
import { media } from 'TMDesignSystem/responsive';
import { Bold, RegText } from 'TMComponents/Typography/styles';

const PageContainer = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 ${spacing.medium};

    ${media.tablet} {
        padding: 0 ${spacing.large};
    }
`;

const HeaderContent = styled.div`
    margin: ${spacing.small} 0 ${spacing.large} 0;
`;

const AuthorsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing.xxLarge};
    margin: ${spacing.xxLarge} 0;
`;

const AuthorCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing.medium};
    background-color: ${({ theme }) => theme.colorSystem.UIElements[5]};
    border: 2px solid ${({ theme }) => theme.colorSystem.UIElements[6]};
    border-radius: ${borderRadius.small};
    padding: ${spacing.mediumLarge};
`;

const AuthorPhoto = styled.div`
    width: ${spacing.xxLarge};
    height: ${spacing.xxLarge};
    border-radius: ${borderRadius.full};
    overflow: hidden;
    flex-shrink: 0;

    ${media.tablet} {
        width: ${spacing.huge};
        height: ${spacing.huge};
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const AuthorContent = styled.div`
    flex: 1;
`;

const AuthorName = styled.h3`
    ${Bold};
    font-size: 1.75rem;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.tiny} 0;

    ${media.tablet} {
        font-size: 2rem;
    }

    span {
        color: ${({ theme }) => theme.colorSystem.secondary[3]};
    }
`;

const JobTitle = styled.p`
    ${Bold};
    ${RegText};
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.medium} 0;
    font-weight: 700 !important;
`;

const Bio = styled.div`
    ${RegText};
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.medium} 0;

    p {
        margin: 0 0 ${spacing.medium} 0;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const ReadMore = styled(NavLink)`
    ${Bold};
    ${RegText};
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    display: inline-flex;
    align-items: center;
    gap: ${spacing.small};
    text-decoration: none;
    font-weight: 700 !important;

    svg {
        width: ${spacing.mediumLarge};
        height: ${spacing.large};
    }
`;

const MeetTheExpertsPage = ({ data }) => {
    const { setIsNavHidden } = useContext(LayoutContext);
    const { setShowBanner } = useContext(BannerContext);
    const { setCrumbs } = useContext(BreadcrumbsContext);

    useLayoutEffect(() => {
        setIsNavHidden(false);
        setShowBanner(true);
        setCrumbs({
            show: true,
            crumbList: [
                {
                    title: 'Meet the experts',
                    link: '/blog/meet-the-experts/'
                }
            ]
        });
    }, [setIsNavHidden, setShowBanner, setCrumbs]);

    return (
        <Main
            isBannerAllowed
            isCrumbsAllowed
            reducedCrumbSpacing
            whiteBackground>
            <HeadTags tags={data.pageContent.seoMetaTags.tags} />
            <Canonical href="blog/meet-the-experts" />
            <PageContainer>
                {data.pageContent.heroContent && (
                    <HeaderContent>
                        <Content
                            content={data.pageContent.heroContent}
                            leftAlign
                            noSidePadding
                        />
                    </HeaderContent>
                )}

                <AuthorsGrid>
                    {data.authors.nodes.map((author) => (
                        <AuthorCard key={author.id}>
                            <AuthorPhoto>
                                <img
                                    src={
                                        getImage(author.photo)?.images
                                            ?.fallback?.src
                                    }
                                    alt={author.name}
                                />
                            </AuthorPhoto>
                            <AuthorContent>
                                <AuthorName>
                                    {author.name}
                                    <span>.</span>
                                </AuthorName>
                                <JobTitle>{author.jobTitle}</JobTitle>
                                <Bio
                                    dangerouslySetInnerHTML={{
                                        __html: (
                                            author.longBio ||
                                            `${author.jobTitle} with expertise in financial planning and budgeting.`
                                        )
                                            .replace(/\n\n/g, '</p><p>')
                                            .replace(/^(.*)$/, '<p>$1</p>')
                                    }}
                                />
                                <ReadMore
                                    href={`/blog/meet-the-experts/${author.slug}/`}>
                                    Read articles by {author.name}
                                    <Arrow color="currentColor" />
                                </ReadMore>
                            </AuthorContent>
                        </AuthorCard>
                    ))}
                </AuthorsGrid>
            </PageContainer>
        </Main>
    );
};

export const pageQuery = graphql`
    {
        pageContent: datoCmsMeetTheExpertsPage {
            seoMetaTags {
                ...GatsbyDatoCmsSeoMetaTags
            }
            heroContent {
                ... on DatoCmsTextStructured {
                    ...textStructuredFields
                }
            }
        }

        authors: allDatoCmsAuthor(
            filter: { showOnMeetTheExpertsPage: { eq: true } }
            sort: { name: ASC }
        ) {
            nodes {
                id
                name
                slug
                jobTitle
                email
                longBio

                photo {
                    gatsbyImageData(
                        width: 64
                        height: 64
                        layout: CONSTRAINED
                        placeholder: NONE
                    )
                }
            }
        }
    }
`;

MeetTheExpertsPage.propTypes = {
    data: shape({
        pageContent: shape({}),
        authors: shape({
            nodes: arrayOf(shape({}))
        })
    }).isRequired
};

export default MeetTheExpertsPage;
